import { publishEvent } from './sync';

export interface Community {
  id: string;
  name: string;
  description: string;
  creatorId: string;
  creatorUsername: string;
  createdAt: number;
  memberCount: number;
}

export interface CommunityMember {
  communityId: string;
  userId: string;
  username: string;
  joinedAt: number;
}

export interface CommunityPin {
  communityId: string;
  videoHash: string;
  pinnedBy: string;
  pinnedAt: number;
}

const COMMUNITIES_KEY = 'authenticulture_communities';
const MEMBERS_KEY = 'authenticulture_community_members';
const COMMUNITY_PINS_KEY = 'authenticulture_community_pins';

export function createCommunity(
  name: string,
  description: string,
  creatorId: string,
  creatorUsername: string
): Community {
  const community: Community = {
    id: crypto.randomUUID(),
    name,
    description,
    creatorId,
    creatorUsername,
    createdAt: Date.now(),
    memberCount: 1
  };

  const communities = getAllCommunities();
  communities.push(community);
  localStorage.setItem(COMMUNITIES_KEY, JSON.stringify(communities));

  // Add creator as member
  joinCommunity(community.id, creatorId, creatorUsername);

  return community;
}

export function getAllCommunities(): Community[] {
  const stored = localStorage.getItem(COMMUNITIES_KEY);
  return stored ? JSON.parse(stored) : [];
}

export function deleteCommunity(communityId: string, userId: string): boolean {
  const communities = getAllCommunities();
  const index = communities.findIndex(c => c.id === communityId && c.creatorId === userId);
  if (index === -1) return false;
  communities.splice(index, 1);
  localStorage.setItem(COMMUNITIES_KEY, JSON.stringify(communities));
  return true;
}

export function getCommunity(id: string): Community | null {
  return getAllCommunities().find(c => c.id === id) || null;
}

export function joinCommunity(communityId: string, userId: string, username: string): void {
  const members = getAllMembers();
  
  // Check if already member
  if (members.find(m => m.communityId === communityId && m.userId === userId)) {
    return;
  }

  members.push({
    communityId,
    userId,
    username,
    joinedAt: Date.now()
  });

  localStorage.setItem(MEMBERS_KEY, JSON.stringify(members));

  // Update member count
  const communities = getAllCommunities();
  const community = communities.find(c => c.id === communityId);
  if (community) {
    community.memberCount = getCommunityMembers(communityId).length;
    localStorage.setItem(COMMUNITIES_KEY, JSON.stringify(communities));
  }
}

export function leaveCommunity(communityId: string, userId: string): void {
  const members = getAllMembers();
  const index = members.findIndex(m => m.communityId === communityId && m.userId === userId);
  
  if (index !== -1) {
    members.splice(index, 1);
    localStorage.setItem(MEMBERS_KEY, JSON.stringify(members));

    const communities = getAllCommunities();
    const community = communities.find(c => c.id === communityId);
    if (community) {
      community.memberCount = getCommunityMembers(communityId).length;
      localStorage.setItem(COMMUNITIES_KEY, JSON.stringify(communities));
    }
  }
}

export function getCommunityMembers(communityId: string): CommunityMember[] {
  const members = getAllMembers();
  return members.filter(m => m.communityId === communityId);
}

export function getAllMembers(): CommunityMember[] {
  const stored = localStorage.getItem(MEMBERS_KEY);
  return stored ? JSON.parse(stored) : [];
}

export function isMember(communityId: string, userId: string): boolean {
  return getAllMembers().some(m => m.communityId === communityId && m.userId === userId);
}

export function pinToCommunity(communityId: string, videoHash: string, userId: string): void {
  const pins = getAllCommunityPins();
  
  // Check if already pinned by this user
  if (pins.find(p => p.communityId === communityId && p.videoHash === videoHash && p.pinnedBy === userId)) {
    return;
  }

  pins.push({
    communityId,
    videoHash,
    pinnedBy: userId,
    pinnedAt: Date.now()
  });

  localStorage.setItem(COMMUNITY_PINS_KEY, JSON.stringify(pins));
}

export function getCommunityPins(communityId: string): CommunityPin[] {
  const pins = getAllCommunityPins();
  return pins.filter(p => p.communityId === communityId);
}

export function getAllCommunityPins(): CommunityPin[] {
  const stored = localStorage.getItem(COMMUNITY_PINS_KEY);
  return stored ? JSON.parse(stored) : [];
}

export function getUserCommunities(userId: string): Community[] {
  const members = getAllMembers();
  const communityIds = new Set(members.filter(m => m.userId === userId).map(m => m.communityId));
  const communities = getAllCommunities();
  return communities.filter(c => communityIds.has(c.id));
}

export function saveRemoteCommunity(community: Community): void {
  const communities = getAllCommunities();
  if (communities.find(c => c.id === community.id)) return;
  communities.push(community);
  localStorage.setItem(COMMUNITIES_KEY, JSON.stringify(communities));
}

const COMMUNITY_EVENTS_CACHE_KEY = 'authenticulture_community_events';
const CHANNEL_MSGS_PREFIX = 'authenticulture_channel_messages';
const COMMUNITY_POSTS_PREFIX = 'authenticulture_community_posts';

export interface ChannelMessage {
  id: string;
  communityId: string;
  channelId: string;
  content: string;
  userId: string;
  pubkey: string;
  magnetURI?: string;
  attachmentType?: 'image' | 'video';
  thumbnailDataUrl?: string;
  replyToId?: string;
  timestamp: number;
}

export interface CommunityPost {
  id: string;
  communityId: string;
  content: string;
  userId: string;
  pubkey: string;
  magnetURI?: string;
  timestamp: number;
}

export function storeCommunityEvent(event: any): void {
  const stored = localStorage.getItem(COMMUNITY_EVENTS_CACHE_KEY);
  const events: any[] = stored ? JSON.parse(stored) : [];
  if (!events.find((e: any) => e.id === event.id)) {
    events.push(event);
    localStorage.setItem(COMMUNITY_EVENTS_CACHE_KEY, JSON.stringify(events));
  }
}

export function getCommunityEventCache(): any[] {
  const stored = localStorage.getItem(COMMUNITY_EVENTS_CACHE_KEY);
  return stored ? JSON.parse(stored) : [];
}

export function storeChannelMessage(msg: ChannelMessage): void {
  const key = `${CHANNEL_MSGS_PREFIX}_${msg.communityId}_${msg.channelId}`;
  const stored = localStorage.getItem(key);
  const msgs: ChannelMessage[] = stored ? JSON.parse(stored) : [];
  if (!msgs.find(m => m.id === msg.id)) {
    msgs.push(msg);
    localStorage.setItem(key, JSON.stringify(msgs.slice(-500)));
  }
}

export function getChannelMessages(communityId: string, channelId: string): ChannelMessage[] {
  const key = `${CHANNEL_MSGS_PREFIX}_${communityId}_${channelId}`;
  const stored = localStorage.getItem(key);
  return stored ? JSON.parse(stored) : [];
}

export function getAllChannelMagnetURIs(): string[] {
  const uris: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (!key?.startsWith(CHANNEL_MSGS_PREFIX)) continue;
    try {
      const msgs: ChannelMessage[] = JSON.parse(localStorage.getItem(key) || '[]');
      for (const msg of msgs) {
        if (msg.magnetURI) uris.push(msg.magnetURI);
      }
    } catch {}
  }
  return Array.from(new Set(uris));
}

export function storeCommunityPost(post: CommunityPost): void {
  const key = `${COMMUNITY_POSTS_PREFIX}_${post.communityId}`;
  const stored = localStorage.getItem(key);
  const posts: CommunityPost[] = stored ? JSON.parse(stored) : [];
  if (!posts.find(p => p.id === post.id)) {
    posts.push(post);
    localStorage.setItem(key, JSON.stringify(posts.slice(-200)));
  }
}

export function getCommunityPosts(communityId: string): CommunityPost[] {
  const key = `${COMMUNITY_POSTS_PREFIX}_${communityId}`;
  const stored = localStorage.getItem(key);
  return stored ? JSON.parse(stored) : [];
}

export async function publishCommunityJoin(communityId: string): Promise<void> {
  await publishEvent('community_join', { communityId } as any);
}

export async function publishCommunityLeave(communityId: string): Promise<void> {
  await publishEvent('community_leave', { communityId } as any);
}

export async function publishModGrant(communityId: string, targetPubkey: string): Promise<void> {
  await publishEvent('mod_grant', { communityId, targetPubkey } as any);
}

export async function publishModRevoke(communityId: string, targetPubkey: string): Promise<void> {
  await publishEvent('mod_revoke', { communityId, targetPubkey } as any);
}

export async function publishCommunityBan(communityId: string, targetPubkey: string): Promise<void> {
  await publishEvent('community_ban', { communityId, targetPubkey } as any);
}

export async function publishCommunityUnban(communityId: string, targetPubkey: string): Promise<void> {
  await publishEvent('community_unban', { communityId, targetPubkey } as any);
}

export async function publishChannelCreate(communityId: string, channelId: string, channelName: string): Promise<void> {
  await publishEvent('channel_create', { communityId, channelId, channelName } as any);
}

export async function publishChannelDelete(communityId: string, channelId: string): Promise<void> {
  await publishEvent('channel_delete', { communityId, channelId } as any);
}

export async function publishChannelMessage(
  communityId: string,
  channelId: string,
  content: string,
  replyToId?: string,
  magnetURI?: string,
  attachmentType?: 'image' | 'video',
  thumbnailDataUrl?: string
): Promise<void> {
  const payload: any = { communityId, channelId, content };
  if (replyToId) payload.replyToId = replyToId;
  if (magnetURI) payload.magnetURI = magnetURI;
  if (attachmentType) payload.attachmentType = attachmentType;
  if (thumbnailDataUrl) payload.thumbnailDataUrl = thumbnailDataUrl;
  await publishEvent('channel_message', payload as any);
}

export async function publishCommunityPost(communityId: string, content: string, magnetURI?: string): Promise<void> {
  const payload: any = { communityId, content };
  if (magnetURI) payload.magnetURI = magnetURI;
  await publishEvent('community_post', payload as any);
}

export async function publishCommunityInvite(communityId: string, targetPubkey: string): Promise<void> {
  await publishEvent('community_invite', { communityId, targetPubkey } as any);
}

export async function publishCommunityLock(communityId: string): Promise<void> {
  await publishEvent('community_lock', { communityId } as any);
}

export async function publishCommunityUnlock(communityId: string): Promise<void> {
  await publishEvent('community_unlock', { communityId } as any);
}
